/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import 'moment-duration-format';

// Component imports
import { Button, Card, Checkbox, Col, Dropdown, Form, Grid, Input, Menu, Popover, Row, Space, Spin, Table, Tooltip, Typography } from 'antd';
import { CaretDownOutlined, CaretUpOutlined, CloseOutlined, DownloadOutlined, FilterOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import ResponsiveTagsList from '../UI/ResponsiveTagsList';
import ChatSession from '../UI/ChatSession/ChatSession';
import VariableDropdown from '../Forms/Fields/RichTextEditor/VariableDropdown';
import ChatSessionDetails from '../UI/ChatSession/ChatSessionDetails';
import BotAvatar from '../UI/BotAvatar';
import DeploymentChannel from '../UI/DeploymentChannel';
import ObservatoryFiltersRow, { useInitialTenantFilters, useObservatoryFilters, useObservatorySearchParams } from '../UI/ObservatoryFiltersRow';
import { ArrowRightIcon } from '../Icons/ArrowRightIcon';
import { ArrowDownIcon } from '../Icons/ArrowDownIcon';

// Misc imports
import { useFetchChatSessionInfoQuery, useFetchChatSessionsQuery, useLazyDownloadChatSessionQuery, useLazyDownloadChatSessionsQuery } from '../../store/chatSessions/chatSessionsApi';
import { isEmptyArray } from '../../utils/is/isEmptyArray';
import { BOT_DEPLOYMENTS_CHANNELS, CHAT_SESSION_MESSAGE_TYPES, DEFAULT_DATETIME_FORMAT, DEFAULT_PAGINATION_SIZE, TAG_SCOPES, VARIABLE_TYPES } from '../../constants/main';
import { useFetchBotDeploymentsQuery } from '../../store/botDeployments/botDeploymentsApi';
import { useFetchTagsQuery } from '../../store/tags/tagsApi';
import { LIVECHAT_EVENT_TYPES } from '../../constants/liveChat';
import { truncate } from '../../utils/string/truncate';
import { useDragDetection } from '../../utils/hooks/useDragDetection';
import { useFetchOrganizationQuery } from '../../store/organization/organizationApi';
import { useFetchStoryVariablesQuery } from '../../store/story/storyApi';
import { useFetchBotsQuery } from '../../store/bots/botsApi';
import is from '../../utils/is';
import useDebouncedCallback from '../../utils/hooks/useDebounce';
import { IS_CHAT_SESSION_INTERACTION_METADATA_ENABLED } from '../../constants/env';

// Constant declarations
const { useBreakpoint } = Grid;
const { Text } = Typography;

const SEARCH_BY_TYPES = {
  UUID: 'uuid',
  SEARCH_TERM: 'searchTerm',
  TAG: 'tag',
  CONTACT: 'contact'
};

const ChatSessionsList = (props) => {
  const { hideAgentsPicker } = props;
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const { orgId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const isMobileOrTabletSize = (screens.xs || screens.sm || screens.md || screens.lg) && !screens.xl;

  const searchInputRef = useRef(null);

  const [activeBotDeployment, setActiveBotDeployment] = useState();
  const [expandedRows, setExpandedRows] = useState([]);
  const [isSearchTypePopoverOpen, setIsSearchTypePopoverOpen] = useState(false);
  const [isSearchFiltersPopoverOpen, setIsSearchFiltersPopoverOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_SIZE);
  const [isSessionVisible, setIsSessionVisible] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState();
  const [isCardAnimating, setIsCardAnimating] = useState(false);
  const [variableDropdownPosition, setVariableDropdownPosition] = useState({ top: 0, left: 0 });
  const [tenantId, setTenantId] = useState(null);
  const [selectedChatSessionMessage, setSelectedChatSessionMessage] = useState({});

  const { data: organization } = useFetchOrganizationQuery({ orgId }, { skip: !orgId });
  const { data: bots, isFetching: isFetchingBots } = useFetchBotsQuery({ orgId, urlParams: { includeBotDeployments: true } }, { skip: !orgId });

  const orgTimezone = organization?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const observatorySearchParams = useObservatorySearchParams(orgTimezone);
  const observatoryFilters = useObservatoryFilters(orgTimezone);
  const initialTenantFilters = useInitialTenantFilters();

  const { currentData: chatsession, isFetching: isFetchingChatSession } = useFetchChatSessionInfoQuery({
    orgId,
    chatSessionId: currentSessionId,
    urlParams: { includeSummary: true } }, {
    skip: !currentSessionId
  });

  const [downloadChatSessions, { isLoading: isDownloadingChatSessions }] = useLazyDownloadChatSessionsQuery();
  const [downloadChatSession, { isLoading: isDownloadingChatSession }] = useLazyDownloadChatSessionQuery();
  const { data: storyVariables } = useFetchStoryVariablesQuery({ orgId, urlParams: { tenantsFilter: observatorySearchParams.tenantsFilter } }, { skip: !orgId });
  const { data: botDeployments, isLoading: isLoadingBotDeployments } = useFetchBotDeploymentsQuery({ orgId, tenantId }, { skip: !orgId || !tenantId });
  const { currentData: tags, isFetching: isFetchingTags } = useFetchTagsQuery({ orgId, urlParams: { scope: TAG_SCOPES.CHAT_SESSIONS } });
  const { data: chatsessions, isFetching: isFetchingChatSessions, refetch: fetchChatSessions } = useFetchChatSessionsQuery({
    orgId,
    urlParams: {
      ...observatorySearchParams,
      timeZone: encodeURIComponent(orgTimezone),
      page: currentPage-1,
      pageSize: pageSize,
      sortBy: searchParams.get('sortBy') || 'createdAt',
      order: searchParams.get('order') || 'DESC',
      separator: searchParams.get('separator') || '%2c',
      fileType: searchParams.get('fileType') || '',
      deploymentIds: searchParams.get('deploymentIds') || '',
      ...(searchParams.get('searchTerm') && { searchTerm: searchParams.get('searchTerm') }),
      ...(searchParams.get('searchVariable') && { searchVariable: searchParams.get('searchVariable') }),
      ...(searchParams.get('uuid') && { uuid: searchParams.get('uuid') }),
      ...(searchParams.get('tags') && { tags: searchParams.get('tags') }),
      ...(searchParams.get('contact') && { contact: searchParams.get('contact') }),
      matchFullWord: searchParams.get('matchFullWord') || false,
      searchByVariable: searchParams.get('searchByVariable') || false,
      isRegex: searchParams.get('isRegex') || false,
      containsFallback: searchParams.get('containsFallback') || false,
      containsFeedback: searchParams.get('containsFeedback') || false,
      containsLiveChat: searchParams.get('containsLiveChat') || false,
      containsCSATSurvey: searchParams.get('containsCSATSurvey') || false,
      csatSurveysStatuses: searchParams.get('containsCSATSurvey') ? ['ANSWERED'] : [],
      includeTenant: true
    }
  }, { skip: !observatorySearchParams.startDate || !observatorySearchParams.endDate || !organization });
  const { handleMouseDown, isDragging } = useDragDetection();
  const searchTypePopoverRef = useRef(null);
  const sessionVariables = storyVariables?.filter(variable => variable.type === 'SESSION');

  const sortingInfo = useMemo(() => {
    let order = searchParams.get('order') ? (searchParams.get('order') === 'DESC' ? 'descend' : 'ascend') : 'descend';
    let columnKey = searchParams.get('sortBy') ? searchParams.get('sortBy') : 'createdAt';

    return { order, columnKey };
  }, [searchParams.get('order'), searchParams.get('sortBy')]);

  // On mount
  useEffect(() => {
    updateUrlSearchParams(
      { current: Number(searchParams.get('page')) + 1, pageSize: Number(searchParams.get('pageSize')) || pageSize },
      { [sortingInfo.columnKey]: sortingInfo.order === 'descend' ? 'DESC' : 'ASC' }
    );
  }, [orgTimezone]);

  useEffect(() => {
    if (isSessionVisible && !isMobileOrTabletSize) {
      setIsCardAnimating(true);
    } else {
      setTimeout(() => setIsCardAnimating(false), 400); // Match this duration with 'chat-session-card-anim' transition time
    }
  }, [isSessionVisible, isMobileOrTabletSize]);

  useEffect(() => {
    if (isSearchTypePopoverOpen && searchTypePopoverRef.current) {
      const rect = searchTypePopoverRef.current?.getBoundingClientRect();
      setVariableDropdownPosition({ bottom: rect.bottom });
    }
  }, [isSearchTypePopoverOpen]);

  useEffect(() => {
    setTenantId(chatsession?.tenant?.id);
  }, [chatsession]);

  const handleSetSelectedChatSessionMessage = useCallback((message) => {
    setSelectedChatSessionMessage(message);
  }, [chatsession]);

  useEffect(() => {
    setSelectedChatSessionMessage({});
  }, [currentSessionId]);

  const isSearchFiltersActive = useMemo(() => {
    const containsFallback = searchParams.get('containsFallback') || false;
    const containsFeedback = searchParams.get('containsFeedback') || false;
    const containsLiveChat = searchParams.get('containsLiveChat') || false;
    const containsCSATSurvey = searchParams.get('containsCSATSurvey') || false;
    return containsCSATSurvey || containsFallback || containsFeedback || containsLiveChat || false;
  }, [searchParams]);

  const columns = [
    {
      title: t('Agent'),
      dataIndex: 'tenant',
      key: 'tenant',
      render: tenant => <BotAvatar bot={tenant} avatarSize={16} />
    },
    {
      title: t('Created At'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: {
        compare: (a, b) => a.createdAt - b.createdAt
      },
      sortOrder: sortingInfo.columnKey === 'createdAt' && sortingInfo.order,
      width: isSessionVisible ? 105 : 140,
      render: createdAt => {
        return (
          moment(createdAt).tz(orgTimezone).format(DEFAULT_DATETIME_FORMAT)
        );
      }
    },
    {
      title: t('Last Interaction'),
      dataIndex: 'lastInteraction',
      key: 'lastInteraction',
      responsive: ['xxl'],
      hidden: isSessionVisible,
      width: 140,
      render: lastInteraction => {
        return (
          moment(lastInteraction).tz(orgTimezone).format(DEFAULT_DATETIME_FORMAT)
        );
      }
    },
    {
      title: t('Contact'),
      dataIndex: 'metadata',
      key: 'metadata',
      ellipsis: {
        showTitle: false
      },
      render: metadata => {
        let contactInfo = '';

        if (metadata?.subscriberName && metadata?.subscriberEmail) {
          contactInfo = `${metadata.subscriberName} (${metadata.subscriberEmail})`;
        } else if (metadata?.subscriberEmail) {
          contactInfo = metadata.subscriberEmail;
        } else if (metadata?.subscriberName) {
          contactInfo = metadata.subscriberName;
        }

        return (
          <Tooltip placement="topLeft" title={contactInfo}>
            {contactInfo}
          </Tooltip>
        );
      }
    },
    {
      title: t('Messages'),
      dataIndex: 'messageCount',
      key: 'messageCount',
      responsive: ['md'],
      hidden: !screens.xxl,
      width: isSessionVisible ? 85 : 120,
      render: messageCount => {
        return isSessionVisible ? messageCount : `${messageCount} ${t('messages')}`;
      }
    },
    {
      title: t('Deployment'),
      dataIndex: 'metadata',
      key: 'metadata',
      responsive: ['md'],
      hidden: isSessionVisible,
      render: metadata => metadata?.deploymentName ? (
        <DeploymentChannel
          deploymentChannelTitle={metadata?.deploymentName}
          {...metadata?.deploymentPlatform ? {
            deploymentChannelIcon: Object.values(BOT_DEPLOYMENTS_CHANNELS).find(
              platform => platform.key === metadata?.deploymentPlatform
            )?.icon
          } : {}}
        />
      ) : <Text className="text-secondary">{t('(Not available)')}</Text>
    },
    {
      title: t('Duration'),
      dataIndex: 'duration',
      key: 'duration',
      responsive: ['xl'],
      hidden: isSessionVisible,
      width: 100,
      render: duration => {
        return (
          moment.duration(duration, 'seconds').format('hh:mm:ss', { trim: false })
        );
      }
    },
    {
      title: t('Tags'),
      dataIndex: 'tags',
      key: 'tags',
      responsive: ['sm'],
      hidden: isSessionVisible,
      render: (_text, record) => {
        if (!record.tags || isEmptyArray(record.tags)) {
          return '-';
        }

        return (
          <ResponsiveTagsList
            values={record.tags.map((tag, index) => ({ label: tag, value: index }))}
            mode="tags"
            className="w-100"
            maxTagCount={1}
            tagRender={(tag) => tag.value === 0 && tag.label.length > 30 ? (
              <Tooltip title={tag.label} placement="bottom">
                <Text className="ant-select-selection-item p-0 px-1 mr-2">{truncate(tag.label, 15, '...')}</Text>
              </Tooltip>
            ) : <Text className="ant-select-selection-item p-0 px-1 mr-2">{tag.label}</Text>}
          />
        );
      }
    },
    {
      title: t('Actions'),
      key: 'actions',
      fixed: 'right',
      align: 'center',
      width: 80,
      render: (_text, record) => {
        return (
          <div onClick={(e) => e.stopPropagation()} className={'d-flex align-items-center justify-content-center'}>
            <Dropdown overlay={downloadFormatsMenu(record)} trigger={['click']}>
              <Tooltip overlayClassName="pointer-events-none" title={t('Download Record')}>
                <Button
                  type="text"
                  size="small"
                  disabled={isDownloadingChatSession}
                  shape="circle"
                  icon={<DownloadOutlined/>}
                />
              </Tooltip>
            </Dropdown>

            <Tooltip overlayClassName="pointer-events-none" title={t('View Record')}>
              <Button
                type="link"
                size="small"
                className={classNames('ml-2', {
                  'bg-primary text-white': record.id === currentSessionId,
                  'bg-skeleton': record.id !== currentSessionId
                })}
                disabled={isDownloadingChatSession}
                icon={<ArrowRightIcon />}
                shape="circle"
                hidden={isMobileOrTabletSize}
                onClick={() => {
                  if (record.id === chatsession?.id) {
                    handleSessionClose();
                  } else {
                    onFetchChatSession(record);
                    setIsSessionVisible(true);
                  }
                }}
              />
            </Tooltip>
          </div>
        );
      }
    }
  ].filter(item => !item.hidden);

  const handleMenuClick = (e, record) => {
    record?.id ? onSingleSessionDownload(e.key, record?.id) : onDownload(e.key);
  };

  const focusSearchInput = () => {
    if(searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleSearchOptionsOnClick = (searchByType, options = {}) => {
    const defaultOptions = {
      uuid: undefined,
      tag: undefined,
      matchFullWord: false,
      isRegex: false,
      contact: undefined
    };
    const { selectedVariable, ...rest } = options;
    const mergedOptions = { ...defaultOptions, ...rest };

    form.setFieldsValue({
      searchBy: searchByType,
      searchTerm: '',
      searchVariable: selectedVariable ? selectedVariable : '',
      ...mergedOptions
    });
    setIsSearchTypePopoverOpen(false);
    updateUrlSearchParams();
    debouncedOnSearchFocus();
  };

  const handleSessionClose = () => {
    setIsSessionVisible(false);
    setCurrentSessionId(null);
  };

  const downloadFormatsMenu = (record) => (
    <Menu
      onClick={(e) => handleMenuClick(e, record)}
      items={[
        {
          label: t('Choose your preferred format'),
          type: 'group'
        },
        {
          label: t('TXT'),
          key: 'txt'
        },
        {
          label: t('CSV'),
          key: 'csv'
        },
        {
          label: t('XLSX'),
          key: 'xlsx'
        }
      ]}
    />
  );

  const onDownload = async (fileType) => {
    await downloadChatSessions({
      orgId: orgId,
      urlParams: {
        ...observatorySearchParams,
        timeZone: encodeURIComponent(orgTimezone),
        page: currentPage-1,
        pageSize: pageSize,
        sortBy: searchParams.get('sortBy') || 'createdAt',
        order: searchParams.get('order') || 'DESC',
        separator: searchParams.get('separator') || '%2c',
        deploymentIds: searchParams.get('deploymentIds') || '',
        fileType: fileType,
        searchTerm: searchParams.get('searchTerm') || '',
        searchVariable: searchParams.get('searchVariable') || '',
        tags: searchParams.get('tags') || '',
        isRegex: searchParams.get('isRegex') || false,
        searchByVariable: searchParams.get('searchByVariable') || false,
        containsFallback: searchParams.get('containsFallback') || false,
        containsCSATSurvey: searchParams.get('containsCSATSurvey') || false,
        csatSurveysStatuses: searchParams.get('containsCSATSurvey') ? ['ANSWERED'] : [],
        matchFullWord: searchParams.get('matchFullWord') || false,
        containsFeedback: searchParams.get('containsFeedback') || false,
        containsLiveChat: searchParams.get('containsLiveChat') || false
      }
    });
  };

  const onSingleSessionDownload = async (fileType, id) => {
    await downloadChatSession({
      orgId: orgId,
      chatSessionId: id,
      urlParams: {
        tenantsFilter: observatorySearchParams.tenantsFilter,
        fileType
      }
    });
  };

  const updateUrlSearchParams = (pagination = { current: 1, pageSize: pageSize || DEFAULT_PAGINATION_SIZE }, sorting = { createdAt: 'DESC' }, filters) => {
    const { current, pageSize } = pagination;
    setCurrentPage(current);
    setPageSize(pageSize);
    const urlSearchParams = new URLSearchParams(location.search);

    const searchBy = form.getFieldValue('searchBy');
    const searchTerm = form.getFieldValue('searchTerm');
    const uuid = form.getFieldValue('uuid');
    const matchFullWord = form.getFieldValue('matchFullWord');
    const isRegex = form.getFieldValue('isRegex');
    const searchVariable = form.getFieldValue('searchVariable');
    const tag = form.getFieldValue('tag');
    const contact = form.getFieldValue('contact');

    const containsFallback = form.getFieldValue('fallback');
    const containsFeedback = form.getFieldValue('feedback');
    const containsLiveChat = form.getFieldValue('livechat');
    const containsCSATSurvey = form.getFieldValue('csatSurvey');

    if (filters?.metadata) {
      urlSearchParams.set('deploymentIds', encodeURIComponent(filters.metadata));
    } else {
      if(filters?.metadata === null) {
        urlSearchParams.set('deploymentIds', encodeURIComponent(''));
      }
    }

    if (searchBy) {
      urlSearchParams.set('searchBy', encodeURIComponent(searchBy));
    } else {
      urlSearchParams.delete('searchBy');
    }

    if (searchTerm) {
      urlSearchParams.set('searchTerm', encodeURIComponent(searchTerm));
    } else {
      urlSearchParams.set('searchTerm', encodeURIComponent(''));
    }

    if (searchVariable) {
      urlSearchParams.set('searchVariable', encodeURIComponent(searchVariable));
    } else {
      urlSearchParams.delete('searchVariable');
    }

    if (uuid) {
      urlSearchParams.set('uuid', encodeURIComponent(uuid));
    } else {
      urlSearchParams.delete('uuid');
    }

    if (contact) {
      urlSearchParams.set('contact', encodeURIComponent(contact));
    } else {
      urlSearchParams.delete('contact');
    }

    if (matchFullWord) {
      urlSearchParams.set('matchFullWord', encodeURIComponent(matchFullWord));
    } else {
      urlSearchParams.delete('matchFullWord');
    }

    if (isRegex) {
      urlSearchParams.set('isRegex', encodeURIComponent(isRegex));
    } else {
      urlSearchParams.delete('isRegex');
    }

    if (tag) {
      urlSearchParams.set('tags', encodeURIComponent(tag));
    } else {
      urlSearchParams.delete('tags');
    }

    // If user didn't add a variable value, don't include variable related search
    if (searchVariable && searchTerm) {
      urlSearchParams.set('searchByVariable', encodeURIComponent(true));
    } else {
      urlSearchParams.delete('searchByVariable');
    }

    if (containsFallback) {
      urlSearchParams.set('containsFallback', encodeURIComponent(containsFallback));
    } else {
      urlSearchParams.delete('containsFallback');
    }

    if (containsFeedback) {
      urlSearchParams.set('containsFeedback', encodeURIComponent(containsFeedback));
    } else {
      urlSearchParams.delete('containsFeedback');
    }

    if (containsLiveChat) {
      urlSearchParams.set('containsLiveChat', encodeURIComponent(containsLiveChat));
    } else {
      urlSearchParams.delete('containsLiveChat');
    }

    if (containsCSATSurvey) {
      urlSearchParams.set('containsCSATSurvey', encodeURIComponent(containsCSATSurvey));
    } else {
      urlSearchParams.delete('containsCSATSurvey');
    }

    urlSearchParams.set('page', current - 1);
    urlSearchParams.set('pageSize', pageSize);

    if (sorting) {
      urlSearchParams.set('sortBy', Object.entries(sorting).flat()[0]);
      urlSearchParams.set('order', Object.entries(sorting).flat()[1]);
    } else {
      urlSearchParams.delete('sortBy');
      urlSearchParams.delete('order');
    }

    // Set the filters to the url
    navigate({
      pathname: location.pathname,
      search: urlSearchParams.toString()
    }, { replace: true });

    // Close expanded session when filters change
    handleSessionClose();
  };

  const debouncedOnChange = useDebouncedCallback(updateUrlSearchParams);
  const debouncedOnSearchFocus = useDebouncedCallback(focusSearchInput, 100);

  const onFetchChatSession = async (record) => {
    setExpandedRows( state => ([
      ...state,
      record ? record.id : null
    ]));
    setCurrentSessionId(record?.id);

    const botDeployment = (botDeployments?.items || []).find((deployment) => deployment.id === record.metadata?.deploymentId);
    if (botDeployment) {
      setActiveBotDeployment({
        ...botDeployment,
        platformIcon: Object.values(BOT_DEPLOYMENTS_CHANNELS).find((channel) => channel.key === botDeployment.platform)?.icon,
        platformName: Object.values(BOT_DEPLOYMENTS_CHANNELS).find((channel) => channel.key === botDeployment.platform).name
      });
    }

    const firstMatch = document.querySelector(`#session-${record.id} .bg-highlight-alt`);
    return firstMatch?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    let sorting = { [searchParams.get('sortBy')]: searchParams.get('order') };
    if (sorter.column) {
      sorting = { [sorter.columnKey]: sorter.order === 'ascend' ? 'ASC' : 'DESC' };
    } else {
      sorting = {
        createdAt: searchParams.get('sortBy') === 'createdAt' && searchParams.get('order') === 'DESC' ? 'ASC' : 'DESC'
      };
    }
    updateUrlSearchParams(pagination, sorting, filters);
  };

  const renderChatSessionHeader = (record) => {
    return (
      <Row justify={'space-between'} align={'middle'}>
        <Col>
          <Space split={<Text strong>{'|'}</Text>}>
            <Space>
              <Text strong>{t('Created at: ')}</Text>
              <Text>{moment(record.createdAt).tz(orgTimezone).format(DEFAULT_DATETIME_FORMAT)}</Text>
            </Space>
            <Space>
              <Text strong>{t('Last Interaction: ')}</Text>
              <Text>{moment(record.lastInteraction).tz(orgTimezone).format(DEFAULT_DATETIME_FORMAT)}</Text>
            </Space>
          </Space>
        </Col>

        <Col>
          <Tooltip title={t('Download Record')}>
            <Dropdown overlay={downloadFormatsMenu(record)} trigger={['click']}>
              <Button
                type="text"
                shape="circle"
                disabled={isFetchingChatSessions}
                block={isMobileOrTabletSize}
                icon={<DownloadOutlined />}
              />
            </Dropdown>
          </Tooltip>

          <Tooltip title={t('Close')}>
            <Button
              type="text"
              shape="circle"
              onClick={() => {
                handleSessionClose();
              }}
              icon={<CloseOutlined />}
            />
          </Tooltip>
        </Col>
      </Row>
    );
  };

  const renderSearchInputPlaceholder = () => {
    const searchBy = form.getFieldValue('searchBy');
    const matchFullWord = form.getFieldValue('matchFullWord');
    const isRegex = form.getFieldValue('isRegex');
    const searchVariable = form.getFieldValue('searchVariable');

    if (isRegex) {
      return t('Search by regular expression');
    }
    if (matchFullWord) {
      return t('Search by exact word');
    }
    if (searchBy === SEARCH_BY_TYPES.UUID) {
      return t('Search by session id');
    }
    if (searchVariable) {
      return `${t('Search by')} ${form.getFieldValue('searchVariable')}`;
    }
    if (searchBy === SEARCH_BY_TYPES.TAG) {
      return t('Search by tag');
    }
    if (searchBy === SEARCH_BY_TYPES.CONTACT) {
      return t('Search by contact');
    }
    return t('Search by text');
  };

  return (
    <Fragment>
      <Form
        layout={isMobileOrTabletSize ? 'vertical' : 'inline'}
        name="chatSessionDates"
        initialValues={{
          tenantsFilter: initialTenantFilters,
          rangePicker: [observatoryFilters.startDate, observatoryFilters.endDate],
          searchTerm: searchParams.get('searchTerm') ? decodeURIComponent(searchParams.get('searchTerm')) : '',
          searchVariable: searchParams.get('searchVariable') ? decodeURIComponent(searchParams.get('searchVariable')) : '',
          uuid: searchParams.get('uuid') ? decodeURIComponent(searchParams.get('uuid')) : '',
          contact: searchParams.get('contact') ? decodeURIComponent(searchParams.get('contact')) : '',
          tag: searchParams.get('tags') ? decodeURIComponent(searchParams.get('tags')) : '',
          matchFullWord: searchParams.get('matchFullWord') || false,
          isRegex: searchParams.get('isRegex') || false,
          searchByVariable: searchParams.get('searchByVariable') || false,
          fallback: searchParams.get('containsFallback') || false,
          feedback: searchParams.get('containsFeedback') || false,
          livechat: searchParams.get('containsLiveChat') || false,
          containsCSATSurvey: searchParams.get('containsCSATSurvey') || false,
          csatSurveysStatuses: searchParams.get('containsCSATSurvey') ? ['ANSWERED'] : [],
          searchBy: searchParams.get('searchBy') || SEARCH_BY_TYPES.SEARCH_TERM
        }}
        form={form}
      >
        <div className="w-100">
          <ObservatoryFiltersRow
            form={form}
            rangePickerProps={{
              disabled: isFetchingChatSessions || isDownloadingChatSessions,
              timezone: orgTimezone
            }}
            agentsPickerProps={{
              agentsList: bots?.items || [],
              disabled: isFetchingChatSessions || isDownloadingChatSessions,
              loading: isFetchingBots,
              hidden: hideAgentsPicker
            }}
          />

          <Row gutter={24}>
            <Col flex="auto">
              <Row gutter={[8, 8]} wrap={false}>
                <Col flex="auto" className={isMobileOrTabletSize ? '' : 'max-field-width'}>
                  <Form.Item
                    name={
                      form.getFieldValue('searchBy') === SEARCH_BY_TYPES.UUID
                        ? SEARCH_BY_TYPES.UUID : form.getFieldValue('searchBy') === SEARCH_BY_TYPES.TAG
                          ? SEARCH_BY_TYPES.TAG : form.getFieldValue('searchBy') === SEARCH_BY_TYPES.CONTACT
                            ? SEARCH_BY_TYPES.CONTACT : SEARCH_BY_TYPES.SEARCH_TERM
                    }
                    noStyle
                  >
                    <Input
                      size="large"
                      allowClear
                      ref={searchInputRef}
                      addonAfter={
                        <Fragment>
                          <Popover
                            open={isSearchTypePopoverOpen}
                            onOpenChange={setIsSearchTypePopoverOpen}
                            placement="bottom"
                            trigger="click"
                            overlayStyle={{ maxWidth: 260 }}
                            content={(
                              <Form
                                layout="vertical"
                                form={form}
                                name="filterOptions"
                                initialValues={{
                                  searchBy: !!searchParams.get('searchBy'),
                                  uuid: !!searchParams.get('uuid'),
                                  tag: !!searchParams.get('tags'),
                                  contact: !!searchParams.get('contact'),
                                  matchFullWord: !!searchParams.get('matchFullWord'),
                                  isRegex: !!searchParams.get('isRegex'),
                                  searchByVariable: !!searchParams.get('searchByVariable'),
                                  fallback: !!searchParams.get('containsFallback'),
                                  feedback: !!searchParams.get('containsFeedback'),
                                  livechat: !!searchParams.get('containsLiveChat'),
                                  csatSurvey: !!searchParams.get('containsCSATSurvey')
                                }}
                              >
                                <Button
                                  type="text"
                                  block
                                  className={`fw-400 mb-1 ${form.getFieldValue('searchBy') === SEARCH_BY_TYPES.SEARCH_TERM && !form.getFieldValue('matchFullWord') && !form.getFieldValue('isRegex') && !form.getFieldValue('searchVariable') ? 'bg-body' : ''}`}
                                  onClick={() => handleSearchOptionsOnClick(SEARCH_BY_TYPES.SEARCH_TERM)}
                                >
                                  {t('Search by text')}
                                </Button>

                                <Button
                                  type="text"
                                  block
                                  className={`fw-400 mb-1 ${form.getFieldValue('searchBy') === SEARCH_BY_TYPES.SEARCH_TERM && form.getFieldValue('matchFullWord') ? 'bg-body' : ''}`}
                                  onClick={() => handleSearchOptionsOnClick(SEARCH_BY_TYPES.SEARCH_TERM, { matchFullWord: true })}
                                >
                                  {t('Search by exact word')}
                                </Button>

                                <Button
                                  type="text"
                                  block
                                  className={`fw-400 mb-1 ${form.getFieldValue('searchBy') === SEARCH_BY_TYPES.SEARCH_TERM && form.getFieldValue('isRegex') ? 'bg-body' : ''}`}
                                  onClick={() => handleSearchOptionsOnClick(SEARCH_BY_TYPES.SEARCH_TERM, { isRegex: true })}
                                >
                                  {t('Search by regular expression')}
                                </Button>

                                <Button
                                  type="text"
                                  block
                                  className={`fw-400 ${form.getFieldValue('searchBy') === SEARCH_BY_TYPES.UUID ? 'bg-body' : ''}`}
                                  onClick={() => handleSearchOptionsOnClick(SEARCH_BY_TYPES.UUID)}
                                >
                                  {t('Search by session id')}
                                </Button>

                                <Button
                                  type="text"
                                  block
                                  className={`fw-400 mb-1 ${form.getFieldValue('searchBy') === SEARCH_BY_TYPES.TAG ? 'bg-body' : ''}`}
                                  onClick={() => handleSearchOptionsOnClick(SEARCH_BY_TYPES.TAG)}
                                >
                                  {t('Search by tag')}
                                </Button>

                                <Button
                                  type="text"
                                  block
                                  className={`fw-400 ${form.getFieldValue('searchBy') === SEARCH_BY_TYPES.CONTACT ? 'bg-body' : ''}`}
                                  onClick={() => handleSearchOptionsOnClick(SEARCH_BY_TYPES.CONTACT)}
                                >
                                  {t('Search by contact')}
                                </Button>

                                <Dropdown
                                  overlay={isSearchTypePopoverOpen ? (
                                    <VariableDropdown
                                      selectedVariable={form.getFieldValue('searchVariable')}
                                      showActivity={false}
                                      storyVariables={sessionVariables}
                                      onAfterAdd={(variable) => {
                                        handleSearchOptionsOnClick(SEARCH_BY_TYPES.SEARCH_TERM, { searchByVariable: true, selectedVariable: variable });
                                      }}
                                      includedVariableTypes={[VARIABLE_TYPES.SESSION]}
                                      truncateCount={0}
                                    />
                                  ) : <Fragment/>}
                                  // https://github.com/ant-design/ant-design/issues/11942#issuecomment-417195388
                                  align={{ offset: [variableDropdownPosition.bottom + 32, -32] }}
                                >
                                  <Row
                                    align="middle"
                                    className={`p-0 ${form.getFieldValue('searchBy') === SEARCH_BY_TYPES.SEARCH_TERM && form.getFieldValue('searchVariable') ? 'bg-body' : ''}`}
                                  >
                                    <Button
                                      onClick={e => e.preventDefault()}
                                      type="text"
                                      block
                                      className="fw-400"
                                    >
                                      {t('Search by variable')}
                                      <ArrowRightIcon className="right-0 position-absolute p-1 text-link text-strong" />
                                    </Button>
                                  </Row>
                                </Dropdown>
                              </Form>
                            )}
                          >
                            <Button
                              ref={searchTypePopoverRef}
                              className="text-secondary bg-none"
                              type="link"
                              icon={isSearchTypePopoverOpen ? <CaretUpOutlined className="fs-sm" /> : <CaretDownOutlined className="fs-sm" />}
                              onClick={() => setIsSearchTypePopoverOpen(!isSearchTypePopoverOpen)}
                            />
                          </Popover>

                          <Popover
                            open={isSearchFiltersPopoverOpen}
                            onOpenChange={setIsSearchFiltersPopoverOpen}
                            placement="bottom"
                            trigger="click"
                            content={(
                              <Form
                                layout="vertical"
                                form={form}
                                name="filterOptions"
                                initialValues={{
                                  uuid: !!searchParams.get('uuid'),
                                  contact: !!searchParams.get('contact'),
                                  matchFullWord: !!searchParams.get('matchFullWord'),
                                  isRegex: !!searchParams.get('isRegex'),
                                  tag: !!searchParams.get('tags'),
                                  searchByVariable: !!searchParams.get('searchByVariable'),
                                  fallback: !!searchParams.get('containsFallback'),
                                  feedback: !!searchParams.get('containsFeedback'),
                                  livechat: !!searchParams.get('containsLiveChat'),
                                  csatSurvey: !!searchParams.get('containsCSATSurvey')
                                }}
                              >
                                <Form.Item name="fallback" valuePropName="checked" className="mb-1">
                                  <Checkbox
                                    checked={form.getFieldValue('fallback')}
                                    onChange={()=>updateUrlSearchParams()}
                                  >
                                    {t('Contains Default Fallback')}
                                  </Checkbox>
                                </Form.Item>

                                <Form.Item name="feedback" valuePropName="checked" className="mb-1">
                                  <Checkbox
                                    checked={form.getFieldValue('feedback')}
                                    onChange={()=>updateUrlSearchParams()}
                                  >
                                    {t('Contains User Feedback')}
                                  </Checkbox>
                                </Form.Item>

                                <Form.Item name="livechat" valuePropName="checked" className="mb-0">
                                  <Checkbox
                                    checked={form.getFieldValue('livechat')}
                                    onChange={()=>updateUrlSearchParams()}
                                  >
                                    {t('Contains LiveChat')}
                                  </Checkbox>
                                </Form.Item>

                                <Form.Item name="csatSurvey" valuePropName="checked" className="mb-0">
                                  <Checkbox
                                    checked={form.getFieldValue('csatSurvey')}
                                    onChange={()=>updateUrlSearchParams()}
                                  >
                                    {t('Contains CSAT Response')}
                                  </Checkbox>
                                </Form.Item>
                              </Form>
                            )}
                          >
                            <Button
                              className={`${isSearchFiltersActive ? 'text-primary' : 'text-secondary'} bg-none`}
                              type="link"
                              icon={
                                <FilterOutlined
                                  className="fs-sm"
                                />
                              }
                              onClick={() => setIsSearchFiltersPopoverOpen(!isSearchFiltersPopoverOpen)}
                            />
                          </Popover>
                        </Fragment>
                      }
                      placeholder={renderSearchInputPlaceholder()}
                      prefix={<SearchOutlined className="text-muted" />}
                      value={form.getFieldValue('searchTerm')}
                      onChange={(e) => {
                        debouncedOnChange();
                        form.setFieldsValue({ search: e.target.value });
                      }}
                      className={isMobileOrTabletSize ? 'w-100' : ''}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>

            <Col flex="none" className={isMobileOrTabletSize ? 'mt-2' : ''}>
              <Row gutter={[8, 8]} justify={isMobileOrTabletSize ? 'start' : 'end'} wrap={false}>
                <Col flex="none">
                  <Dropdown overlay={downloadFormatsMenu} trigger={['click']}>
                    <Tooltip title={t('Download')}>
                      <Button
                        size="large"
                        disabled={isFetchingChatSessions}
                        icon={<DownloadOutlined />}
                      />
                    </Tooltip>
                  </Dropdown>
                </Col>

                <Col flex="none">
                  <Tooltip title={t('Refresh List')}>
                    <Button
                      size="large"
                      type="primary"
                      disabled={isFetchingChatSessions}
                      icon={<ReloadOutlined />}
                      onClick={() => {
                        handleSessionClose();
                        fetchChatSessions();
                      }}
                    />
                  </Tooltip>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      </Form>

      <Row gutter={4} className="chat-session-wrapper mt-2">
        <Col className="chat-session-table-anim" style={{ width: isSessionVisible && !isMobileOrTabletSize ? '33%' : '100%' }}>
          <Table
            size="small"
            rowKey="id"
            scroll={{ y: 680 }}
            className="scroll-table"
            rowClassName={(record) => {
              return record.id === chatsession?.id ? 'cursor-pointer bg-table-row-selected' : 'cursor-pointer';
            }}
            onRow={(record) => ({
              onMouseDown: handleMouseDown,
              onClick: () => {
                if(!isDragging()) {
                  if (record.id === chatsession?.id) {
                    handleSessionClose();
                    setExpandedRows(
                      expandedRows.filter( row => {
                        return row !== record.id;
                      })
                    );
                  } else {
                    onFetchChatSession(record);
                    setIsSessionVisible(true);
                  }
                }
              }
            })}
            columns={columns}
            onChange={handleTableChange}
            pagination={{
              defaultCurrent: 1,
              current: currentPage,
              defaultPageSize: chatsessions?.pageSize || pageSize || DEFAULT_PAGINATION_SIZE,
              total: chatsessions?.total || 0,
              size: 'default',
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: handleSessionClose,
              showTotal: (total) => `${t('Total Chat Sessions')}: ${total}`
            }}
            dataSource={chatsessions && is.array(chatsessions.items) && chatsessions.items}
            loading={isFetchingChatSessions || isLoadingBotDeployments}
            expandedRowKeys={expandedRows}
            expandable={!isMobileOrTabletSize ? false : {
              expandIcon: ({ expanded, onExpand, record }) => {
                return (
                  <Button
                    type="link"
                    size="small"
                    className={`${expanded ? 'bg-primary text-white' : 'bg-skeleton'}`}
                    icon={<ArrowDownIcon />}
                    shape="circle"
                    onClick={e => onExpand(record, e)}
                  />
                );
              },
              onExpand: (expanded, record) => {
                if (expanded) {
                  onFetchChatSession(record);
                } else {
                  handleSessionClose();
                  setExpandedRows(
                    expandedRows.filter( row => {
                      return row !== record.id;
                    })
                  );
                }
              },
              expandedRowRender: record => {
                return chatsession ? (
                  <Fragment>
                    <ChatSessionDetails
                      chatSession={chatsession}
                      tags={tags}
                      isFetchingChatSession={isFetchingChatSession}
                      isFetchingTags={isFetchingTags}
                      activeBotDeployment={activeBotDeployment}
                      onFetchChatSession={onFetchChatSession}
                      selectedChatSessionMessage={selectedChatSessionMessage}
                    />

                    <ChatSession
                      id={'session-' + record.id}
                      isInteractionMetadataEnabled={IS_CHAT_SESSION_INTERACTION_METADATA_ENABLED}
                      handleSetSelectedChatSessionMessage = {handleSetSelectedChatSessionMessage}
                      sessionTenantId={record?.tenant?.id}
                      session={
                        chatsession?.messages && chatsession?.messages.filter((message) =>
                          (
                            message.liveChatEventType === LIVECHAT_EVENT_TYPES.LIVECHAT_REQUEST_ACCEPTED
                            || message.liveChatEventType === LIVECHAT_EVENT_TYPES.LIVECHAT_TERMINATED
                            || message.liveChatEventType === LIVECHAT_EVENT_TYPES.LIVECHAT_REQUEST_CLOSED
                            || message.type !== 'TRIGGER'
                          ) && message.message)
                      }
                      loading={isFetchingChatSession && !chatsession?.messages}
                      wrapperClassName="border-radius-base"
                      searchTerm={form.getFieldValue('searchTerm')}
                      timezone={orgTimezone}
                    />
                  </Fragment>
                ) : null;
              },
              rowExpandable: record => isMobileOrTabletSize
            }}
          />
        </Col>

        {
          (isSessionVisible && (chatsession || isFetchingChatSession || isCardAnimating) && !isMobileOrTabletSize) ? (
            <Col
              className={'chat-session-card-anim'}
              style={{
                width: isSessionVisible ? '66%' : '0%',
                opacity: isSessionVisible ? 1 : 0.2
              }}
            >
              <Card
                className="ml-2 border-0 h-100"
                title={chatsession ? renderChatSessionHeader(chatsession): null}
                size="small"
                headStyle={{ backgroundColor: '#DDDFE9', height: '40px', borderRadius: '8px 8px 0px 0px' }}
                style={{ minHeight: '720px', maxHeight: '720px' }}
              >
                {
                  chatsession?.messages && chatsession && tags ? (
                    <Row>
                      <Col style={{ width: '50%', maxHeight: 658 }}>
                        <ChatSession
                          id={'session-' + chatsession.id}
                          isInteractionMetadataEnabled={IS_CHAT_SESSION_INTERACTION_METADATA_ENABLED}
                          handleSetSelectedChatSessionMessage = {handleSetSelectedChatSessionMessage}
                          sessionTenantId={chatsession?.tenant?.id}
                          session={
                            chatsession?.messages && chatsession?.messages.filter((message) => (
                              message.liveChatEventType === LIVECHAT_EVENT_TYPES.LIVECHAT_REQUEST_ACCEPTED
                                    || message.liveChatEventType === LIVECHAT_EVENT_TYPES.LIVECHAT_TERMINATED
                                    || message.liveChatEventType === LIVECHAT_EVENT_TYPES.LIVECHAT_REQUEST_CLOSED
                                    || message.type !== CHAT_SESSION_MESSAGE_TYPES.TRIGGER)
                                  && message.message
                            ).slice(0).reverse()
                          }
                          loading={isFetchingChatSession && !chatsession?.messages}
                          style={{ height: 586 }}
                          className="overflow-y-auto chat-session-list scrollbar-custom px-4 pt-8 overflow-x-hidden"
                          searchTerm={form.getFieldValue('searchTerm')}
                          wrapperClassName="border-radius-base"
                          timezone={orgTimezone}
                        />
                      </Col>

                      <Col style={{ width: '50%', maxHeight: 658 }}>
                        <ChatSessionDetails
                          selectedChatSessionMessage={selectedChatSessionMessage}
                          chatSession={chatsession}
                          tags={tags}
                          isFetchingChatSession={isFetchingChatSession}
                          isFetchingTags={isFetchingTags}
                          activeBotDeployment={activeBotDeployment}
                          onFetchChatSession={onFetchChatSession}
                        />
                      </Col>
                    </Row>
                  ) : (
                    <Row className="w-100">
                      <Col flex="auto" className="text-center">
                        <Spin className="mt-10 mb-8"/>
                      </Col>
                    </Row>
                  )
                }
              </Card>
            </Col>
          ) : null
        }
      </Row>
    </Fragment>
  );
};

ChatSessionsList.propTypes = {
  hideAgentsPicker: PropTypes.bool
};

export default ChatSessionsList;
